import React, {
    useContext,
    useState,
    useRef,
    useLayoutEffect,
    useEffect
} from "react";
import {
    Spinner,
    MessageBar,
    MessageBarType,
    IconButton
} from "@fluentui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DataContext } from "../DataContext";
import { ChatMessage, IAskAiResponse } from "../../services/AiService";
import useMobileDetector from "../useMobileDetector";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { OrbUserContext } from "../OrbUserContext";
import { IEntityDescription1 } from "../../types";
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { CrCommandBar } from "../cr/CrCommandBar";
import { ConfirmDialog } from "../cr/ConfirmDialog";
import { CrLoadingOverlay } from "../cr/CrLoadingOverlay";
import { decodeRouteId, encodeRouteId } from "../../utils/routeId";

const formatMsgTime = (iso?: string): string => {
    if (!iso) return "";
    const d = new Date(iso);

    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear().toString().slice(2);
    const hours = d.getHours() % 12 || 12;
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = d.getHours() >= 12 ? "PM" : "AM";

    return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
};

interface IAiChatProps {
    onEntityDescriptionChange?: (desc: IEntityDescription1) => void;
    lastTopicIdForChilds?: number | null;
}

const AiChat: React.FC<IAiChatProps> = (props) => {
    const history = useHistory();
    const location = useLocation();
    const { chatId } = useParams<{ chatId: string }>();

    const actualChatId = decodeRouteId(chatId);

    const returnTo = React.useMemo(() => {
        const params = new URLSearchParams(location.search);
        const value = params.get('returnTo');
        return value ? decodeURIComponent(value) : undefined;
    }, [location.search]);

    const { userContext } = useContext(OrbUserContext);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [chatTitle, setChatTitle] = useState<string | null>(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [error, setError] = useState<string | null>(null);

    const isMobile = useMobileDetector();

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const {
        dataServices: { aiService, app04AIChatService }
    } = useContext(DataContext);

    const scrollToBottom = (smooth = true) => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    };

    // Forces WebKit to repaint the scroll container — fixes iOS blank-content bug
    const forceRepaint = () => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.style.transform = "translateZ(0)";
        // reading offsetHeight forces a synchronous reflow
        void container.offsetHeight;
        container.style.transform = "";
    };

    useLayoutEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        // double rAF: wait for iOS to finish painting the new DOM before scrolling
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                forceRepaint();
                scrollToBottom(true);
            });
        });
    }, [messages, loading]);

    useEffect(() => {
        if (!actualChatId) return;

        const loadMessages = async () => {
            try {
                const history = await aiService.getMessages(Number(actualChatId));
                setMessages(history);
            } catch {
                // history load failure is non-fatal, start fresh
            }
        };

        const loadChatTitle = async () => {
            try {
                const chat = await app04AIChatService.read(actualChatId);
                setChatTitle(chat?.Title ?? null);
                props.onEntityDescriptionChange?.({
                    DescriptionLine1: 'AI Chat',
                    DescriptionLine2: chat?.Title ?? '',
                    Description1ForList: null,
                    Description1ForForm: null,
                });
            } catch {
                // non-fatal, menu box just won't show a title
            }
        };

        setIsInitialLoading(true);

        Promise.all([loadMessages(), loadChatTitle()]).finally(() => {
            setIsInitialLoading(false);
        });

    }, [actualChatId]);

    const adjustHeight = (el: HTMLTextAreaElement) => {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(e.target.value);
        adjustHeight(e.target);
    };

    const sendMessage = async () => {
        if (!prompt.trim() || loading) return;

        const userPrompt = prompt;

        // user message
        setMessages(prev => [
            ...prev,
            {
                role: "user",
                content: userPrompt,
                createdOn: new Date().toISOString(),
                userName: userContext.UserTitle
            }
        ]);

        setPrompt("");
        setLoading(true);
        setError(null);

        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }

        try {
            const data: IAskAiResponse = await aiService.ask(userPrompt, Number(actualChatId));

            if (data.summarised && data.summary) {
                // Replace entire message history with summary + new exchange
                setMessages([
                    {
                        role: "summary",
                        content: data.summary,
                        createdOn: new Date().toISOString()
                    },
                    {
                        role: "user",
                        content: userPrompt,
                        createdOn: new Date().toISOString(),
                        userName: userContext.UserTitle
                    },
                    {
                        role: "assistant",
                        content: data.response,
                        createdOn: new Date().toISOString()
                    }
                ]);

                // Fire-and-forget: build/refresh the topic-level summary now that this
                // chat has a fresh chat-level summary. Non-critical — swallow errors.
                aiService.createTopicSummary(Number(actualChatId)).catch(() => { });

            } else {
                setMessages(prev => [
                    ...prev,
                    {
                        role: "assistant",
                        content: data.response,
                        createdOn: new Date().toISOString()
                    }
                ]);
            }

        } catch (err: any) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const confirmClearChat = (): void => {
        app04AIChatService
            .clearChatMessages({ ChatID: actualChatId })
            .then(() => {
                setShowClearConfirm(false);
                setMessages([]);
            });
    };

    const cancelClearChat = (): void => {
        setShowClearConfirm(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // On mobile do NOT intercept Enter at all
        // Let keyboard naturally insert newline
        if (isMobile) {
            return;
        }

        // Desktop behavior
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }

        // Shift+Enter naturally inserts newline
    };

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'BackToChats',
            text: 'Back to Chats',
            iconProps: { iconName: 'Back' },
            onClick: () => {
                if (returnTo) {
                    history.push(returnTo);
                } else if (props.lastTopicIdForChilds != null) {
                    history.push(`/a14/chats-for-topic/${encodeRouteId(props.lastTopicIdForChilds!)}`);
                } else {
                    history.push(`/a14/chats`);
                }
            },
        },
        {
            key: 'ClearChat',
            text: 'Clear',
            disabled: messages.length === 0,
            iconProps: { iconName: 'Broom' },
            onClick: () => {
                setShowClearConfirm(true);
            },
        },
    ];

    return (
        <>
            <ConfirmDialog
                hidden={!showClearConfirm}
                title={`Are you sure you want to clear all messages in '${chatTitle ?? 'this chat'}'?`}
                content="This will permanently delete all messages in this chat. This cannot be undone."
                confirmButtonText="Clear"
                handleConfirm={confirmClearChat}
                handleCancel={cancelClearChat}
            />
            <div className="aiChatContainer">
                <CrLoadingOverlay isLoading={isInitialLoading} opaque={true} />
                <div className="aiChatToolbar">
                    <CrCommandBar items={commandBarItems} />
                </div>

                <div className="aiChatMessages" ref={messagesContainerRef} style={{ scrollbarColor: '#f5f5f5 transparent' }}>

                    <div className="aiChatHeader">
                        {chatTitle ?? 'Ask Cloe'}
                    </div>

                    {messages.map((msg, index) => (
                        <div key={index} className={`chatBubbleWrapper ${msg.role}`}>

                            {(
                                <div className="chatBubbleMeta">
                                    {msg.role === "user"
                                        ? `${msg.userName ?? userContext.UserTitle}  ·  ${formatMsgTime(msg.createdOn)}`
                                        : formatMsgTime(msg.createdOn)
                                    }
                                </div>
                            )}

                            <div className={`chatBubble ${msg.role}`}>
                                {msg.role === "summary" ? (
                                    <>
                                        <div className="summaryLabel">Earlier conversation summarised</div>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {msg.content}
                                        </ReactMarkdown>
                                    </>
                                ) : msg.role === "user" ? (
                                    msg.content
                                ) : (
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                    </ReactMarkdown>
                                )}
                            </div>

                        </div>
                    ))}

                    {loading && (
                        <div className="chatBubbleWrapper assistant">
                            <div className="chatBubble assistant">
                                <Spinner label="Thinking..." />
                            </div>
                        </div>
                    )}

                    <div ref={bottomRef} />

                </div>

                {error && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {error}
                    </MessageBar>
                )}

                <div className="aiChatFooter">

                    <div className="aiChatInputRow">

                        <div className="aiChatInput">
                            <textarea
                                ref={textareaRef}
                                className="aiChatTextarea"
                                value={prompt}
                                onChange={handleTextareaChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask Cloe"
                                rows={1}
                            />
                        </div>

                        <IconButton
                            iconProps={{ iconName: "Up" }}
                            onClick={sendMessage}
                            disabled={loading}
                            className="sendButton"
                        />

                    </div>

                    <div className="aiChatDisclaimer">
                        Cloe is AI and can make mistakes.
                    </div>

                </div>

            </div>
        </>
    );
};

export default AiChat;